
import { computed, defineComponent, PropType, reactive, onBeforeMount } from 'vue'

// @components
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import ColorPicker from '@/common/components/UI/ColorPicker/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import { TimeSlotZone, TimeSlotZonePayload } from '@/new-services/types/time-slot-zones'

// @validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    name: 'new-time-slot-zone-form',
    components: {
        NewInput,
        NewSelect,
        ColorPicker,
        NewButton,
    },
    props: {
        dispatcherSectors: {
            type: Array as PropType<DispatcherSector[]>,
            default: () => [],
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<TimeSlotZone>,
            default: null,
        },
        isPaintStarted: {
            type: Boolean,
            default: false,
        },
        isSectorAdded: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel', 'start-paint', 'create', 'update', 'change-color'],
    setup(props, { emit }) {
        const generateRandomHex = (): string => {
            const hex = Math.floor(Math.random() * 16777216).toString(16).padStart(6, '0')
            return `#${hex}`
        }

        const state = reactive({
            name: null as string | null,
            dispatcherSectorId: null as number | null,
            color: generateRandomHex(),
        })

        const state_form$ = useVuelidate(
            {
                name: { required },
                dispatcherSectorId: { required },
                color: { required },
            },
            state
        )

        const onCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const onStartPaint = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('start-paint', {
                color: state.color,
                name: state.name,
            })
        }

        const emitCreate = () => {
            const payload: Omit<TimeSlotZonePayload, 'coordinates'> = {
                name: state.name as string,
                color: state.color as string,
                dispatcherSectorId: state.dispatcherSectorId as number,
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            const payload: Omit<TimeSlotZonePayload, 'coordinates'> = {
                name: state.name as string,
                color: state.color as string,
                dispatcherSectorId: state.dispatcherSectorId as number,
            }

            emit('update', {
                id: props.initialValue.id,
                payload,
            })
        }

        const onSubmit = () => {
            if (props.loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                emitCreate()
            }

            if (props.isUpdate) {
                emitUpdate()
            }
        }

        const onChangeColor = (color: string) => {
            if (props.loading) return
            emit('change-color', color)
        }

        onBeforeMount(() => {
            if (props.isUpdate && props.initialValue) {
                state.color = props.initialValue.color
                state.name = props.initialValue.name
                state.dispatcherSectorId = props.initialValue.dispatcherSectorId
            }
        })

        const modalTitle = computed(() => {
            if (props.isCreate) {
                return 'Добавление сектора тайм-слотов'
            }

            if (props.isUpdate) {
                return 'Редактирование сектора тайм-слотов'
            }

            return 'modal title'
        })

        const buttonTitle = computed(() => {
            if (props.isUpdate) {
                return 'Сохранить сектор тайм-слотов'
            }

            if (props.isCreate) {
                return 'Добавить сектор тайм-слотов'
            }

            return 'button title'
        })

        const isRenderingStartPaint = computed(() => {
            if (props.isUpdate) return false

            if (props.isPaintStarted || props.isSectorAdded) {
                return false
            }

            return true
        })

        const isRenderingOnPaint = computed(() => {
            if (props.isUpdate) return false

            if (props.isPaintStarted && !props.isSectorAdded) {
                return true
            }

            return false
        })

        const isRenderingSubmit = computed(() => {
            if (props.isUpdate) return true

            if (!props.isPaintStarted && props.isSectorAdded) {
                return true
            }

            return false
        })

        return {
            state,
            state_form$,
            //
            onCancel,
            onStartPaint,
            onSubmit,
            onChangeColor,
            //
            modalTitle,
            buttonTitle,
            isRenderingStartPaint,
            isRenderingOnPaint,
            isRenderingSubmit,
        }
    },
})
